import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

const PATIENT_NOT_IN_PRACTICE_STATUS = 404;

const isPatientNotInPracticeError = (error) =>
  error?.response?.status === PATIENT_NOT_IN_PRACTICE_STATUS;

/*
 * The products a new dose can be recorded with (KIN-2126/KIN-2127): in date and not withdrawn on
 * `onDate`, and, unless `showAll` lifts it, licensed for the patient's age on that day. An adult
 * was being offered 6 in 1, PCV and Rotavirus from the whole catalogue (KIN-1964).
 *
 * `onDate` is the vaccination's date given, so a backdated dose is judged on the day it was
 * given. A product with no age range, or a patient with no date of birth, is never filtered on age.
 *
 * Only the picker reads this. Resolving a product by id — an edit, a schedule prefill, a batch —
 * stays on the full catalogue (useHealthlinkVaccines), where a product hidden here still resolves.
 */
export const useActiveVaccineProducts = ({
  patientId,
  onDate,
  showAll = false,
  enabled = true,
}) => {
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const query = useQuery({
    queryKey: ["activeVaccineProducts", tenantVal, patientId, onDate, showAll],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/vaccine-codes/active`,
        { params: { patientId, onDate, showAll } }
      );

      return Array.isArray(response?.data) ? response.data : [];
    },
    enabled: enabled && !!tenantVal && !!patientId && !!onDate,
    // A patient outside this practice stays outside it; asking again only delays the message.
    retry: (failureCount, error) =>
      !isPatientNotInPracticeError(error) && failureCount < 1,
    meta: { errorMessage: "Error fetching vaccine products" },
  });

  return {
    ...query,
    isPatientNotInPractice: isPatientNotInPracticeError(query.error),
  };
};
